import { ConversationDTO } from "@shaarp/common";
import {
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";

/**
 * The periods a conversation list is grouped by, most recent first — the
 * order they are shown in. Each is a key of `chat.page.timeGroups`.
 */
export const CONVERSATION_TIME_GROUPS = [
  "today",
  "yesterday",
  "thisWeek",
  "previousWeek",
  "thisMonth",
  "previousMonth",
  "older",
] as const;

export type ConversationTimeGroup = (typeof CONVERSATION_TIME_GROUPS)[number];

export type ConversationsGroupedByTime = Record<
  ConversationTimeGroup,
  ConversationDTO[]
>;

/**
 * Where each period starts, on the device's own calendar: local midnights and
 * Mondays, so a week or a month that spans the new year stays one, and the
 * answer is the same in every time zone for what the user sees as "Monday".
 */
const periodStarts = (now: Date) => {
  const thisWeek = startOfWeek(now, { weekStartsOn: 1 });
  const thisMonth = startOfMonth(now);
  return {
    today: startOfDay(now),
    yesterday: subDays(startOfDay(now), 1),
    thisWeek,
    previousWeek: subWeeks(thisWeek, 1),
    thisMonth,
    previousMonth: subMonths(thisMonth, 1),
  };
};

const timeGroupOf = (
  lastMessageAt: Date,
  starts: ReturnType<typeof periodStarts>,
): ConversationTimeGroup => {
  if (lastMessageAt >= starts.today) return "today";
  if (lastMessageAt >= starts.yesterday) return "yesterday";
  if (lastMessageAt >= starts.thisWeek) return "thisWeek";
  if (lastMessageAt >= starts.previousWeek) return "previousWeek";
  if (lastMessageAt >= starts.thisMonth) return "thisMonth";
  if (lastMessageAt >= starts.previousMonth) return "previousMonth";
  return "older";
};

/**
 * Sorts conversations into periods by their last message, keeping their order
 * within each period. The first period that holds it wins: yesterday is
 * "yesterday" even on a Monday, and the days of the previous week that fall
 * in this month are "previous week".
 */
export function groupConversationsByTime(
  conversations: readonly ConversationDTO[],
): ConversationsGroupedByTime {
  const grouped: ConversationsGroupedByTime = {
    today: [],
    yesterday: [],
    thisWeek: [],
    previousWeek: [],
    thisMonth: [],
    previousMonth: [],
    older: [],
  };
  const starts = periodStarts(new Date());
  for (const conversation of conversations) {
    grouped[timeGroupOf(new Date(conversation.lastMessageAt), starts)].push(
      conversation,
    );
  }
  return grouped;
}
